import type { Integration } from '@/types/integration';
import axiosInstance from './axiosInstance';

/**
 * Check if brand has Shopify integration
 *
 * This function is designed to be used outside of React components (e.g., in axios interceptors)
 * where hooks cannot be used. For React components, use useShopifyIntegrationQuery() instead.
 *
 * @returns Promise<boolean> - true if Shopify integration exists, false otherwise
 *
 * @example
 * const hasShopify = await hasShopifyIntegration();
 * if (!hasShopify) {
 *   window.location.href = '/onboarding/shopify';
 * }
 */
export async function hasShopifyIntegration(): Promise<boolean> {
  try {
    const response = await axiosInstance.get<Integration[]>('/dashboard/integrations');
    const integrations = response.data;

    // Check if Shopify integration exists (regardless of status)
    return integrations.some((integration) => integration.platform === 'shopify');
  } catch (error) {
    // If we can't fetch integrations, assume no Shopify integration
    // This prevents blocking users if the API is temporarily unavailable
    console.error('[Integration Checker] Failed to fetch integrations:', error);
    return false;
  }
}
